<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import { destroySwiper } from '@/utils/site-utils';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import SiteRichText from '@/elements/SiteRichText.vue';
import ViewportReveal from '@/components/ViewportReveal.vue';
import utils from '@/utils';

const props = defineProps({
  /** @type {HerobannerFields} */
  fields: {
    type: Object,
    default: null,
  },
  rendering: {
    type: Object,
    default: null,
  },
});

let /** @type {Swiper | undefined} */ swiper;
let resizeTimer;
const MOBILE_BREAKPOINT = 840;

const swiperEl = ref(null);
const prevEl = ref(null);
const nextEl = ref(null);
const mobilePrevEl = ref(null);
const mobileNextEl = ref(null);
const paginationEl = ref(null);
const isMobileViewport = ref(false);

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-acc'));
const rootStyle = computed(() => utils.overall?.baseComStyle?.(props.fields) || {});

const CARD_BACKGROUNDS = ['#F3F6F3', '#EEEEE4'];

const showCount = computed(() => {
  const value = props?.fields?.number?.value;
  const parsed = Number(value);
  return parsed > 0 ? parsed : 3;
});

const isTypeReverse = computed(() => !!props?.fields?.typeRever?.value);

const items = computed(() =>
  (props?.fields?.list ?? []).map((item, index) => ({
    ...item,
    button: item?.fields?.button ?? null,
    originalIndex: index,
  }))
);

const chunkedItems = computed(() => {
  const chunks = [];
  for (let i = 0; i < items.value.length; i += showCount.value) {
    chunks.push(items.value.slice(i, i + showCount.value));
  }
  return chunks;
});

const slideItems = computed(() =>
  isMobileViewport.value ? items.value.map((item) => [item]) : chunkedItems.value
);
const getDisplayIndex = (itemIndex) => itemIndex + (isTypeReverse.value ? 0 : 1);

const isFullCard = (itemIndex) => getDisplayIndex(itemIndex) % 2 === 1;

const isMediaFirst = (itemIndex) => Math.floor(getDisplayIndex(itemIndex) / 2) % 2 === 0;

const getCardBackground = (itemIndex) =>
  CARD_BACKGROUNDS[Math.floor(getDisplayIndex(itemIndex) / 2) % CARD_BACKGROUNDS.length];

const getImageValue = (item) => item?.fields?.image?.value ?? item?.fields?.image ?? {};

const shouldShowControls = computed(() => slideItems.value.length > 1);

const syncViewport = () => {
  if (typeof window === 'undefined') return;
  isMobileViewport.value = window.innerWidth < MOBILE_BREAKPOINT;
};

const buildSwiper = () => {
  if (!swiperEl.value) return;

  if (!shouldShowControls.value) {
    destroySwiper(swiper, true, true);
    swiper = undefined;
    return;
  }

  destroySwiper(swiper, true, true);
  swiper = new Swiper(swiperEl.value, {
    modules: [Navigation, Pagination],
    slidesPerView: 1,
    spaceBetween: 16,
    observer: true,
    observeParents: true,
    navigation: {
      prevEl: isMobileViewport.value ? mobilePrevEl.value : prevEl.value,
      nextEl: isMobileViewport.value ? mobileNextEl.value : nextEl.value,
    },
    pagination: {
      el: paginationEl.value,
      clickable: true,
      enabled: isMobileViewport.value,
      bulletElement: 'span',
    },
    breakpoints: {
      840: {
        spaceBetween: 24,
        pagination: {
          enabled: false,
        },
      },
      1200: {
        spaceBetween: 32,
        pagination: {
          enabled: false,
        },
      },
    },
  });
};

const queueBuildSwiper = () => {
  nextTick(() => buildSwiper());
};

const handleResize = () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    syncViewport();
    queueBuildSwiper();
  }, 120);
};

watch(
  () => [slideItems.value.length, showCount.value, isTypeReverse.value, isMobileViewport.value],
  () => {
    queueBuildSwiper();
  }
);

onMounted(() => {
  syncViewport();
  queueBuildSwiper();
  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  clearTimeout(resizeTimer);
  window.removeEventListener('resize', handleResize);
  destroySwiper(swiper, true, true);
  swiper = undefined;
});
</script>

<template>
  <section v-if="fields" :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="c-acc__inner">
        <div class="c-acc__header" :class="{ 'has-controls': shouldShowControls }">
          <ViewportReveal class="c-acc__header-copy">
            <JssRichText :field="fields?.title" tag="h2" />
            <SiteRichText
              class="c-acc__description"
              :field="fields.describe"
              v-if="fields.describe?.value"
            />
          </ViewportReveal>

          <div class="c-acc__controls" v-if="shouldShowControls">
            <button
              ref="prevEl"
              class="c-acc__control c-acc__control--prev"
              type="button"
              aria-label="Previous slide"
            >
              <span class="c-acc__control-icon" />
            </button>
            <button
              ref="nextEl"
              class="c-acc__control c-acc__control--next"
              type="button"
              aria-label="Next slide"
            >
              <span class="c-acc__control-icon" />
            </button>
          </div>
        </div>

        <div ref="swiperEl" class="swiper c-acc__swiper">
          <div class="swiper-wrapper">
            <div
              class="swiper-slide c-acc__slide"
              v-for="(chunk, slideIndex) in slideItems"
              :key="`slide-${slideIndex}`"
            >
              <div
                class="c-acc__grid"
                :class="`is-${showCount}`"
                :style="{ '--acc-columns': showCount }"
              >
                <article
                  v-for="item in chunk"
                  :key="item.id || item.originalIndex"
                  class="c-acc__item"
                  :class="{
                    'is-full': isFullCard(item.originalIndex),
                    'is-split': !isFullCard(item.originalIndex),
                    'is-media-first': isMediaFirst(item.originalIndex),
                    'is-media-last': !isMediaFirst(item.originalIndex),
                  }"
                >
                  <template v-if="isFullCard(item.originalIndex)">
                    <div class="c-acc__full-card">
                      <img
                        v-if="getImageValue(item).src"
                        class="c-acc__full-image"
                        :src="getImageValue(item).src"
                        :alt="getImageValue(item).alt || ''"
                        loading="lazy"
                      />

                      <RichText
                        v-if="item.fields.title?.value"
                        class="c-acc__card-title c-acc__card-title--light c-acc__card-title--overlay"
                        :field="item.fields.title"
                      />

                      <div class="c-acc__full-content">
                        <RichText
                          class="c-acc__card-description c-acc__card-description--light"
                          :field="item.fields.describe"
                        />
                        <SiteButton
                          v-if="item.button?.fields"
                          class="c-acc__button c-acc__button--light"
                          v-bind="item.button"
                        />
                      </div>
                    </div>
                  </template>

                  <template v-else>
                    <div class="c-acc__split-media">
                      <img
                        v-if="getImageValue(item).src"
                        class="c-acc__split-image"
                        :src="getImageValue(item).src"
                        :alt="getImageValue(item).alt || ''"
                        loading="lazy"
                      />
                    </div>

                    <div
                      class="c-acc__split-card"
                      :style="{ backgroundColor: getCardBackground(item.originalIndex) }"
                    >
                      <div class="c-acc__split-copy">
                        <RichText class="c-acc__card-title" :field="item.fields.title" />
                        <RichText class="c-acc__card-description" :field="item.fields.describe" />
                      </div>
                      <SiteButton
                        v-if="item.button?.fields"
                        class="c-acc__button"
                        v-bind="item.button"
                      />
                    </div>
                  </template>
                </article>
              </div>
            </div>
          </div>
        </div>

        <div class="c-acc__mobile-footer" v-if="shouldShowControls">
          <button
            ref="mobilePrevEl"
            class="c-acc__control c-acc__control--prev c-acc__control--mobile"
            type="button"
            aria-label="Previous slide"
          >
            <Icon name="ArrowLeft" />
          </button>

          <div ref="paginationEl" class="c-acc__pagination swiper-pagination" />

          <button
            ref="mobileNextEl"
            class="c-acc__control c-acc__control--next c-acc__control--mobile"
            type="button"
            aria-label="Next slide"
          >
            <Icon name="ArrowRight" />
          </button>
        </div>
      </div>
    </ContentGrid>
  </section>
</template>

<style lang="scss" scoped>
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-acc {
  $c: &;
  background: $white;

  &__inner {
    width: 100%;
    margin: 0 auto;
    padding: 0 grid-width-m(1);
  }
  &__title {
    font-weight: 600;
    :deep(*) {
      font-size: 36px;
      line-height: 1.12;
    }
  }
  &__header {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 40px;
    text-align: center;
    &-copy {
      display: flex;
      flex-direction: column;
      gap: 16px;
      align-items: center;
    }
  }

  &__controls {
    display: none;
    justify-content: flex-end;
    gap: 12px;
  }

  &__mobile-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 24px;
    margin-top: 24px;
  }

  &__control {
    width: 48px;
    height: 48px;
    border: 1px solid rgba(0, 10, 70, 0.16);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(13px);
    color: $blue-night;
    box-shadow: 0 4px 12px rgba(18, 30, 110, 0.06);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition:
      border-color 0.2s ease,
      background-color 0.2s ease,
      color 0.2s ease,
      opacity 0.2s ease,
      box-shadow 0.2s ease;

    :deep(.e-icon),
    :deep(svg) {
      width: 16px;
      height: 16px;
    }

    &:hover {
      border-color: $blue-night;
      background: rgba(255, 255, 255, 0.22);
      color: $blue-night;
      box-shadow: 0 6px 18px rgba(18, 30, 110, 0.12);
    }

    &--mobile {
      flex: 0 0 auto;
    }

    &.swiper-button-disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  &__control-icon {
    width: 10px;
    height: 10px;
    border-top: 2px solid currentColor;
    border-right: 2px solid currentColor;
    transform: rotate(45deg);
  }

  &__control--prev {
    #{$c}__control-icon {
      transform: rotate(-135deg);
      margin-left: 4px;
    }
  }

  &__control--next {
    #{$c}__control-icon {
      margin-right: 4px;
    }
  }

  &__title,
  &__description,
  &__card-title,
  &__card-description,
  &__tip {
    :deep(*:first-child) {
      margin-top: 0;
    }

    :deep(*:last-child) {
      margin-bottom: 0;
    }
  }

  &__description {
    font-size: 16px;
  }

  &__swiper {
    overflow: hidden;
  }

  &__slide {
    height: auto;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  &__item {
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: 100%;
    min-width: 0;
  }

  &__item.is-media-last {
    flex-direction: column-reverse;
    .c-acc__split-media {
      order: 2;
    }
  }

  &__split-media,
  &__full-card {
    position: relative;
    overflow: hidden;
    border-radius: 16px;
  }
  :deep(svg),
  :deep(path) {
    color: $blue-night;
    stroke: $blue-night;
  }
  &__full-card {
    :deep(.e-site-button__text) {
      color: $white;
    }
    :deep(svg),
    :deep(path) {
      color: $white;
      stroke: $white;
    }
  }
  &__split-card {
    position: relative;
    overflow: hidden;
    border-radius: 12px;
    .e-site-link {
      margin-top: auto;
    }
  }

  &__split-media {
    min-height: 320px;
  }

  &__split-card {
    min-height: 182px;
    padding: 18px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
  }

  &__split-copy {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
  }

  &__full-card {
    min-height: 518px;
    padding: 20px 18px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    background: #121e6e;
  }

  &__full-image,
  &__split-image {
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__full-card::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.04) 38%, rgba(0, 0, 0, 0.64) 100%);
  }

  &__full-content {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    max-width: 92%;
  }

  &__tip {
    position: relative;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border-radius: 0;
    background: transparent;
    color: $white;
  }

  &__tip--media,
  &__tip--overlay {
    position: absolute;
    top: 0;
    left: 0;
  }

  &__card-title {
    color: $black;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.35;
    margin: 0;
    :deep(*) {
      display: -webkit-box;
      overflow: hidden;
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      line-clamp: 1;
    }
  }

  &__card-title--overlay {
    position: absolute;
    top: 28px;
    left: 28px;
    right: 28px;
    z-index: 2;
    margin-bottom: 0;

    :deep(*) {
      line-clamp: 2;
    }
  }

  &__card-description {
    color: #4f4f4f;
    font-size: 18px;
    line-height: 1.35;
    :deep(*) {
      display: -webkit-box;
      overflow: hidden;
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      line-clamp: 3;
    }
  }

  &__card-title--light,
  &__card-description--light {
    color: $white;

    :deep(*) {
      color: $white;
    }
  }

  &__button {
    display: inline-flex;
    margin-top: auto;
    padding-top: 14px;
    height: auto;
    border: 0;
    background: transparent;
    color: #6c9f72;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.4;
    text-decoration: none;

    &:hover,
    &:focus {
      background: transparent;
      color: #6c9f72;
      text-decoration: none;
    }

    :deep(.e-site-button__text) {
      line-height: 1.4;
      white-space: nowrap;
    }

    :deep(.e-site-button__icon),
    :deep(.e-site-button__icon svg) {
      width: 14px;
      height: 14px;
      color: $blue-night;
      fill: none;
    }
  }

  &__button--light {
    color: $white;

    &:hover,
    &:focus {
      background: transparent;
      color: $white;
    }

    :deep(.e-site-button__text) {
      color: $white;
    }

    :deep(.e-site-button__icon),
    :deep(.e-site-button__icon svg) {
      color: $white;
      stroke: $white;
    }
  }

  &__pagination {
    position: static;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 48px;

    :deep(.swiper-pagination-bullet) {
      width: 6px;
      height: 6px;
      margin: 0 4px;
      border-radius: 999px;
      background: #d0d8ef;
      opacity: 1;
      transition:
        width 0.2s ease,
        background-color 0.2s ease;
    }

    :deep(.swiper-pagination-bullet-active) {
      width: 24px;
      background: $blue-night;
    }
  }
}

@include tablet {
  .c-acc {
    margin: 0 auto;
    &__inner {
      padding: 0;
    }

    &__header {
      flex-direction: row;
      align-items: flex-start;
      justify-content: space-between;
      gap: 24px;
      margin-bottom: 40px;
      text-align: left;
    }

    &__header:not(.has-controls) {
      justify-content: center;
      text-align: center;
    }

    &__header-copy {
      flex: 1 1 auto;
      align-items: flex-start;
    }

    &__header:not(.has-controls) &__header-copy {
      flex: 0 1 auto;
      align-items: center;
    }

    &__controls {
      display: flex;
      flex: 0 0 auto;
      padding-top: 4px;
    }

    &__mobile-footer {
      display: none;
    }

    &__grid {
      gap: 16px;
      align-items: stretch;
    }

    &__grid.is-2,
    &__grid.is-3,
    &__grid.is-4 {
      grid-template-columns: repeat(var(--acc-columns, 3), minmax(0, 1fr));
    }

    &__item {
      height: 522px;
    }

    &__item.is-split {
      display: grid;
      grid-template-rows: 280px minmax(0, 1fr);
      gap: 16px;
    }

    &__split-media {
      min-height: 0;
      height: 100%;
    }

    &__split-card {
      min-height: 0;
      height: 100%;
      padding: 18px;
      .e-site-link {
        margin-top: auto;
      }
    }

    &__full-card {
      min-height: 0;
      height: 100%;
      padding: 18px;
    }

    &__full-content {
      justify-content: flex-end;
    }
  }
}
</style>
